import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useLanguage } from '@/context/LanguageContext'
import { CreateInvoiceReq } from '@/services/mobi-api/invoice/types'
import { UnitOption } from '@/services/mobi-api/unit/types'
import useIntl from '@/utils/intl'
import { Checkbox, Dialog, TextField } from '@mui/material'
import React, { FunctionComponent } from 'react'
import MCButton from '@/components/ui/Buttons/MobicomButton'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { Close } from '@mui/icons-material'
import { checkEmail, isCorpRegister, isMobile } from '@/utils/helpers'
import { resolveImageUrl } from '@/utils/constants'
import { getSystemEnv } from '@/utils/env'
import { useTheme } from '@/context/ThemeContext'

interface Props {
  handleNext: (payload: CreateInvoiceReq) => void
  invoice: CreateInvoiceReq | undefined
  option: UnitOption | undefined
}

const UnitStep2: FunctionComponent<Props> = ({
  handleNext,
  invoice,
  option
}) => {
  const { locale } = useLanguage()
  const { theme } = useTheme()
  const [email, setEmail] = React.useState<string>()
  const [corpId, setCorpId] = React.useState<string>()
  const [checked, setChecked] = React.useState(false)
  const [vatType, setVatType] = React.useState('CONSUMER')
  const [terms, openTerms] = React.useState(false)
  const { translate } = useIntl()

  const VAT = [
    {
      name: translate('shop.vatCons'),
      icon: '/uploads/icons/user1.svg',
      darkIcon: '/uploads/icons/user.svg',
      type: 'CONSUMER'
    },
    {
      name: translate('shop.vatCorp'),
      icon: '/uploads/icons/briefcase.svg',
      darkIcon: '/uploads/icons/briefcase1.svg',
      type: 'CORPORATE'
    }
  ]

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setChecked(event.target.checked)
  }
  const handleChangeVatType = (value: string): void => {
    setVatType(value)
  }
  const closeModal = (): void => {
    setChecked(true)
    openTerms(false)
  }
  const handleContinue = (): void => {
    const payload: CreateInvoiceReq = {
      customerType: vatType === 'CORPORATE' ? 'CORPORATE' : 'CONSUMER',
      languageCode: locale,
      vatEmail: email,
      corpId: vatType === 'CORPORATE' ? corpId : undefined,
      device: isMobile(),
      redirectUrl: getSystemEnv().DOMAIN + '/paymentStatus'
    }
    handleNext(payload)
  }
  const disableContinue = (): boolean => {
    let disable = false
    if (!checkEmail(email ?? '')) {
      disable = true
    }
    if (vatType === 'CORPORATE' && corpId !== undefined) {
      if (!isCorpRegister(corpId)) {
        disable = true
      }
    }
    if (!checked) {
      disable = true
    }
    if (vatType === '') {
      disable = true
    }
    return disable
  }
  return (
    <div className='bg-surfaceInverse rounded-b-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-between overflow-auto mb-24'>
      <div className='flex-grow overflow-auto'>
        <div className='flex flex-col gap-4 p-4 border-b md:p-8 border-secondaryMidStroke'>
          <span className='text-xs font-bold text-textMid md:text-sm'>
            {translate('order.summary')}
          </span>
          <div className='w-full py-2 border bg-surfaceWebGrey border-secondaryMidStroke rounded-xl'>
            <div className='flex justify-between px-4 py-2'>
              <span className='text-xs font-medium text-textMid md:text-sm'>
                {translate('phoneNumber')}:
              </span>
              <span className='text-xs font-bold text-textHigh md:text-sm'>
                {invoice?.isdn}
              </span>
            </div>
            <div className=' flex justify-between px-4 py-2'>
              <span className='text-xs font-medium text-textMid md:text-sm'>
                {translate('unit')}:
              </span>
              <span className='flex text-xs font-bold truncate text-textHigh md:text-sm md:w-56 w-3/4 justify-end '>
                {option?.descriptionMn}
              </span>
            </div>
            <div className='flex justify-between px-4 py-2'>
              <span className='text-xs font-medium text-textMid md:text-sm'>
                {translate('price')}:
              </span>
              <span className='text-xs font-bold text-textHigh md:text-sm'>
                {option?.price}₮
              </span>
            </div>
          </div>
        </div>
        <div className='p-4 md:p-8'>
          <span className='text-xs font-bold text-textMid md:text-sm'>
            {translate('shop.vat')}
          </span>
          <div className='flex w-full gap-4 mt-4 md:w-96'>
            {VAT.map((vat, index) => (
              <MCButton
                selected={vat.type === vatType}
                type='shop'
                size='sm'
                fullwidth
                onClick={() => handleChangeVatType(vat.type)}
                key={index}
                leadingIconImg={theme === 'dark' ? resolveImageUrl(vat.darkIcon) : resolveImageUrl(vat.icon)}
              >
                {vat.name}
              </MCButton>
            ))}
          </div>
          <div className='w-full md:w-96'>
            <TextField
              value={email}
              id='outlined-basic'
              label={
                vatType === 'CORPORATE'
                  ? translate('shop.corpEmail')
                  : translate('shop.vatEmail')
              }
              variant='outlined'
              onChange={(e) => {
                setEmail(e.target.value)
              }}
              className='w-full mt-4 rounded-lg border-secondaryMainStroke bg-surfaceInverse text-textHigh md:mt-8'
              InputLabelProps={{
                style: { color: 'var(--color-text-textHigh)' }
              }}
              InputProps={{
                style: {
                  color: 'var(--color-text-textHigh)',
                  '&::placeholder': {
                    color: 'var(--color-text-secondaryHighStroke)'
                  }
                } as any
              }}
              sx={{
                '& fieldset': {
                  borderRadius: '8px'
                }
              }}
            />
            {vatType === 'CORPORATE'
              ? (
                <TextField
                  value={corpId}
                  id='outlined-basic'
                  label={translate('shop.vatCorpId')}
                  variant='outlined'
                  onChange={(e) => {
                    setCorpId(e.target.value)
                  }}
                  className='w-full mt-4 rounded-lg border-secondaryMainStroke bg-surfaceInverse text-textHigh'
                  InputLabelProps={{
                    style: { color: 'var(--color-text-textHigh)' }
                  }}
                  InputProps={{
                    style: {
                      color: 'var(--color-text-textHigh)',
                      '&::placeholder': {
                        color: 'var(--color-text-secondaryHighStroke)'
                      }
                    } as any
                  }}
                  sx={{
                    '& fieldset': {
                      borderRadius: '8px'
                    }
                  }}
                />
                )
              : (
                <></>
                )}
          </div>
          <div className='flex items-center mt-10 mb-24'>
            <Checkbox
              color='error'
              checked={checked}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
              className='cursor-pointer text-textHigh'
            />
            <p
              className='text-xs md:text-sm font-medium text-textHigh cursor-pointer hover:underline hover:underline-offset-4 hover:text-[#EF4444]'
              onClick={() => openTerms(true)}
            >
              {translate('acceptTermsOfService')}
            </p>
          </div>
        </div>
      </div>
      <div className='fixed bottom-0 left-0 right-0 z-50 p-4 border-t md:sticky md:p-6 bg-surfaceInverse border-secondaryMidStroke'>
        <DarkButton
          fullwidth
          disabled={disableContinue()}
          onClick={handleContinue}
        >
          {translate('continue')}
        </DarkButton>
      </div>
      <Dialog open={terms}>
        <div className='p-4 bg-surfaceMid'>
          <div className='flex items-center justify-between w-full'>
            <span className='text-lg font-bold text-textMid'>
              {translate('termsOfService')}
            </span>
            <DarkIconButton
              type='text'
              size='sm'
              icon={<Close fontSize='small' />}
              onClick={() => openTerms(false)}
            />
          </div>
          <div className='px-10 py-8 mt-4 border rounded-lg border-slate-200'>
            <p className='text-lg font-bold text-textHigh'>
              {translate('generalProvs')}
            </p>
            <p className='mt-6 text-sm font-medium text-justify text-textMid'>
              {translate('termMain')}
              <br />
              1. {translate('term1')}
              <br />
              2. {translate('term2')}
            </p>
          </div>
          <div className='flex justify-end w-full mt-4'>
            <MCButton size='sm' onClick={closeModal}>
              {translate('accept')}
            </MCButton>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
export default UnitStep2
